<script setup lang="ts">
import {
  computed,
  onMounted,
  onUnmounted,
  reactive,
  Ref,
  ref,
  UnwrapRef,
  watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import api from '@/api';
import { DgrSelectbox } from '@stockmarkteam/donguri-ui';
import AdpDocumentCard from '@/components/common/adp-document-card.vue';
import AdpDocumentInfoDefault from '@/components/common/adp-document/adp-document-info-default.vue';
import AdpDocumentInfo from '@/components/common/adp-document/adp-document-info.vue';
import SelectedTagList from '@/components/common/multi-select/selected-tag-list.vue';
import TagSelect from '@/components/common/multi-select/tag-select.vue';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import SwrScrollingPagination from '@/components/common/swr-scrolling-pagination.vue';
import GroupHeader from '@/components/group/group-header.vue';
import GroupSurveyButton from '@/components/group/group-survey-button.vue';
import Content from '@/components/layouts/content.vue';
import { AdpDocument, isDocType, TagSelectItem } from '@/types';
import { useGroupMark } from '@/utils/actions/group-mark';
import { documentKey } from '@/utils/adpDocument';
import { useAdpDocumentOptionMenu } from '@/utils/composables/useAdpDocumentOptionMenu';
import { selectArticle } from '@/utils/selectArticle';
import {
  STATES,
  useDocument,
  useGroupMarks,
  useGroupMarksCount,
  useGroups,
  useGroupTags,
  useTeamUsers,
  useUserInfo,
} from '@/utils/swr';
import { useEmitter, useStore } from '@/utils/vue';

withDefaults(defineProps<{ isMenuVisible?: boolean }>(), {
  isMenuVisible: true,
});

const pageName = 'group_marks';
const feature = 'group_marks';

const router = useRouter();
const route = useRoute();
const emitter = useEmitter();
const store = useStore();
const { createSnackbar } = useSnackbar();
const { updateTagsAndMemo, removeGroupMark } = useGroupMark({
  trackingBaseData: {
    pageName: pageName,
    pageUrl: route.fullPath,
    feature: feature,
  },
});

const { data: groups, state } = useGroups();
const { data: myInfo } = useUserInfo();

const selectedArticles = reactive({ items: [] as AdpDocument[] });
const selectedArticle = computed<AdpDocument | undefined>(() => {
  if (selectedArticles.items.length === 1) {
    return selectedArticles.items[0];
  }
  return undefined;
});

const memoExists = ref<string>((route.query.memo_exists as string) ?? 'all');
const selectMemoOptions = [
  { value: 'all', label: 'すべての記事' },
  { value: 'true', label: 'グループメモした記事' },
  { value: 'false', label: 'グループメモしていない記事' },
];
const targetTags = ref({
  items: [] as string[],
});

const groupId = computed(() => {
  if (route.params.groupId instanceof Array) {
    return route.params.groupId[0];
  }
  return route.params.groupId;
});
const hasEditInfoPermission = computed(() =>
  myInfo.value !== undefined || group.value !== undefined
    ? myInfo.value!.role === 'admin' || group.value!.is_member
    : false,
);
const { data: groupMarkArticlesAndCount, mutate: mutateGroupMarks } =
  useGroupMarksCount(groupId, memoExists, targetTags);
const markCount = computed(() => groupMarkArticlesAndCount.value?.count);

const docType = computed(() => {
  let docType = null;
  if (route.query.doc_type instanceof Array) {
    docType = route.query.doc_type[0];
  } else {
    docType = route.query.doc_type;
  }
  if (isDocType(docType)) {
    return docType;
  }
  return null;
});
const documentId = computed<string | null>(() => {
  if (route.query.document_id instanceof Array) {
    return route.query.document_id[0];
  }
  return route.query.document_id;
});
const isShowingSpecificDocument = computed(
  () => docType.value && documentId.value,
);
const documentOption = computed(() => ({
  article_lang: route.query.article_lang
    ? (route.query.article_lang as string)
    : undefined,
  patent_publication_office: route.query.patent_publication_office
    ? (route.query.patent_publication_office as string)
    : undefined,
  report_page_number: route.query.report_page_number
    ? Number(route.query.report_page_number)
    : undefined,
  group_id_for_memo_and_tag: Number(groupId.value),
}));
const { data: documentData } = useDocument(documentId, docType, documentOption);

const { data: tagsData, mutate: mutateTags } = useGroupTags(
  groupId,
  'group_mark',
);
const tags = computed<{ tags: TagSelectItem[] }>(() => ({
  tags: tagsData.value?.tags ?? [],
}));

const group = computed(() =>
  (groups.value ?? { groups: [] }).groups.find(
    g => g.id === Number(groupId.value),
  ),
);

const trackPageView = async () => {
  const previousRoute = store.state.route.previousRoute;
  const hasPreviousRoute = !!previousRoute?.name;
  const isSnackbar =
    hasPreviousRoute &&
    String(previousRoute.params.groupId) !== String(groupId.value);
  await api.trackPageView({
    pageName: pageName,
    pageUrl: route.fullPath,
    group: {
      id: Number(route.params.groupId),
    },
    fromUrl: previousRoute?.path ?? null,
    fromSnackbarLink: isSnackbar ? 'article_group_mark' : undefined,
  });
};

const initialize = () => {
  if (validateGroup()) {
    trackPageView();
  }
};

const handleUpdate = (article: AdpDocument) => {
  emitter.emit('pagination-items-update', {
    filterFunc: (i: AdpDocument) => articleFilter(i, article),
    updateFunc: (items: AdpDocument[]) => updateArticle(items, article),
  });
};

onMounted(() => {
  initialize();
  emitter.on('article-updated', handleUpdate);
});

onUnmounted(() => {
  emitter.off('article-updated', handleUpdate);
});

watch(
  () => groupId.value,
  () => initialize(),
);

watch(
  () => state.value,
  () => {
    if (state.value === STATES.SUCCESS) {
      initialize();
    }
  },
);

watch(
  () => route.query,
  () => {
    selectedArticles.items = [];
  },
);

const validateGroup = () => {
  // グループから他のページに移動したらバリデーションが必要ない
  if (groupId.value === undefined) return false;

  if (state.value === STATES.SUCCESS && group.value === undefined) {
    createSnackbar({
      message: '指定されたグループは存在しません',
      type: 'error',
    });
    router.push({ name: 'anewsHome' });
    return false;
  }
  return true;
};

const isSelected = (item: AdpDocument) => {
  return selectedArticles.items.some(i => documentKey(i) === documentKey(item));
};

const enableHover = (article: AdpDocument): boolean | undefined => {
  return !isSelected(article);
};

const toggleSelected = (e: MouseEvent, item: AdpDocument) => {
  selectedArticles.items = selectArticle(e, item, selectedArticles.items);
};

const articleFilter = (article: AdpDocument, target: AdpDocument) => {
  return documentKey(article) === documentKey(target);
};

const updateArticle = (
  articles: AdpDocument[],
  updatedArticle: AdpDocument,
) => {
  const index = articles.findIndex(
    c => documentKey(c) === documentKey(updatedArticle),
  );
  if (index >= 0) {
    articles[index].marks = updatedArticle.marks;
    articles[index].memo = updatedArticle.memo;
    articles[index].tags = updatedArticle.tags;
  }
  return articles;
};

const updateArticleInfo = async (article: AdpDocument) => {
  if (!selectedArticle.value || !group.value) return;

  await updateTagsAndMemo({
    group: group.value,
    adpDocument: selectedArticle.value,
    tags: article.tags,
    memo: article.memo,
  });

  mutateTags();
  emitter.emit('pagination-items-update', {
    filterFunc: (i: AdpDocument) => articleFilter(i, article),
    updateFunc: (items: AdpDocument[]) => updateArticle(items, article),
  });
  emitter.emit('article-updated', article);
};

const deleteArticle = (articles: AdpDocument[], target: AdpDocument) => {
  const index = articles.findIndex(c => documentKey(c) === documentKey(target));
  if (index >= 0) {
    articles.splice(index, 1);
  }
};

const removeGroupMarkWrapper = async (article: AdpDocument) => {
  if (!group.value) return;
  await removeGroupMark({
    adpDocument: article,
    group: group.value,
  });
  mutateGroupMarks();
  mutateTags();
  emitter.emit('pagination-item-delete', (items: AdpDocument[]) =>
    deleteArticle(items, article),
  );
};

const optionMenu = useAdpDocumentOptionMenu(
  { pageName, feature },
  { onRemoveGroupMark: removeGroupMarkWrapper },
);

const handleTagFilterArticles = (tags: string[]) => {
  targetTags.value.items = tags;
};

const removeTag = (tagName: string) => {
  targetTags.value.items = targetTags.value.items.filter(t => t !== tagName);
};

watch(
  () => [targetTags.value.items, memoExists.value],
  async () => {
    const memoFilterValue =
      memoExists.value === 'true'
        ? 'with_memo'
        : memoExists.value === 'false'
          ? 'without_memo'
          : 'all';
    await api.trackEvent(
      'mark_filter',
      {
        pageName: pageName,
        pageUrl: route.fullPath,
        feature: feature,
      },
      undefined,
      Number(groupId.value),
      {
        filter: { tags: targetTags.value.items, memo: memoFilterValue },
      },
    );
  },
);

const getGroupMark = (adpDocument: AdpDocument) => {
  return adpDocument.group_marks?.find(gm => gm.group_id === group.value?.id);
};

const getGroupMarkThemeId = (adpDocument: AdpDocument) => {
  return getGroupMark(adpDocument)?.theme_id;
};

const { data: users } = useTeamUsers();

const getSelectedArticleGroupMarkUser = computed(() => {
  return users.value?.users.find(
    u => u.id === getGroupMark(selectedArticles.items[0])?.creator_id,
  );
});

const paginationFunc = (pageRef: Ref<number>, pageLimit: number) =>
  useGroupMarks(groupId, pageRef, pageLimit, memoExists, targetTags);
const dataAccessor = (d: UnwrapRef<ReturnType<typeof useGroupMarks>['data']>) =>
  d?.marked_articles;
const pageLimit = 10;
</script>

<template>
  <div class="group" v-if="group && myInfo">
    <GroupHeader
      :group="group"
      :user-info="myInfo"
      :is-menu-visible="isMenuVisible"
    />
    <Content>
      <div class="group-content">
        <div class="action-bar">
          <div class="left-side-container">
            <template v-if="!isShowingSpecificDocument">
              <span class="c-title-bold--m"
                >グループマーク<span class="c-text-regular--s" :key="markCount"
                  >・ {{ markCount }}件</span
                ></span
              >
              <GroupSurveyButton
                :group-id="group.id"
                :page-name="pageName"
                class="group-survey-button"
              />
            </template>
          </div>
        </div>
        <div class="filter-container">
          <DgrSelectbox
            class="o-memo-filter"
            v-model="memoExists"
            :options="selectMemoOptions"
            size="small"
          ></DgrSelectbox>
          <TagSelect
            v-if="tags"
            :tag-list="tags.tags"
            :target-tags="targetTags.items"
            @filter-articles="handleTagFilterArticles"
            :empty-filter-message="'指定したグループタグはありません。'"
            :placeholder="`グループタグ ${
              targetTags.items.length > 0 ? targetTags.items.length : ''
            }`"
          ></TagSelect>
        </div>
        <SelectedTagList
          class="selected-tags"
          v-if="targetTags.items.length > 0"
          label="選択中のグループタグ"
          :selected-tags="targetTags.items"
          :is-editing="true"
          :show-tooltip="true"
          @delete-tag="removeTag"
        ></SelectedTagList>
        <div class="mark-page-content">
          <div class="article-area">
            <AdpDocumentCard
              v-if="isShowingSpecificDocument && documentData"
              class="article-card"
              :key="documentId ?? undefined"
              :page-name="pageName"
              :adp-document="documentData"
              :group="group"
              :theme-id="getGroupMarkThemeId(documentData)"
              :feature="feature"
              :show-comment="false"
              :is-selected="isSelected(documentData)"
              :enable-hover="enableHover(documentData)"
              @click="toggleSelected($event, documentData)"
              :option-actions="
                documentData
                  ? optionMenu.buildOptionActions(documentData, {
                      removeGroupMark: true,
                    })
                  : []
              "
              @option-action="
                name =>
                  documentData &&
                  optionMenu.handleOptionAction(name, documentData)
              "
            ></AdpDocumentCard>
            <SwrScrollingPagination
              v-else-if="!isShowingSpecificDocument"
              :key="`${[memoExists, targetTags.items]}`"
              :page-limit="pageLimit"
              :pagination-func="paginationFunc"
              :data-accessor="dataAccessor"
            >
              <template v-slot="{ items, loaded }">
                <AdpDocumentCard
                  class="article-card"
                  v-for="article in items"
                  :key="documentKey(article)"
                  :page-name="pageName"
                  :adp-document="article"
                  :group="group"
                  :theme-id="getGroupMarkThemeId(article)"
                  :feature="feature"
                  :show-comment="false"
                  :is-selected="isSelected(article)"
                  :enable-hover="enableHover(article)"
                  @click="toggleSelected($event, article)"
                  :option-actions="
                    optionMenu.buildOptionActions(article, {
                      removeGroupMark: true,
                    })
                  "
                  @option-action="
                    name => optionMenu.handleOptionAction(name, article)
                  "
                  :show-tag-list="true"
                  :priority-display-tags="targetTags.items"
                ></AdpDocumentCard>
                <div class="no-marks" v-if="loaded && items.length === 0">
                  <div class="content">
                    <div class="c-text-regular--m">
                      {{
                        markCount === 0
                          ? '該当する記事はありませんでした。'
                          : 'お探しの条件に合う記事がありません'
                      }}
                    </div>
                  </div>
                  <img src="@/assets/state-empty-article.png" />
                </div>
              </template>
            </SwrScrollingPagination>
          </div>
          <div class="side-content">
            <div class="side-content-body">
              <AdpDocumentInfo
                class="article-info"
                v-if="selectedArticles.items.length === 1"
                :editable="hasEditInfoPermission"
                :adp-document="selectedArticles.items[0]"
                :creator="getSelectedArticleGroupMarkUser"
                :tag-list="tags.tags"
                @article-info-updated="updateArticleInfo"
                :is-group-view="true"
                :page-name="pageName"
              ></AdpDocumentInfo>
              <AdpDocumentInfoDefault
                :is-group-view="true"
                :adp-document="selectedArticles.items"
                :page-name="pageName"
                v-else
              ></AdpDocumentInfoDefault>
            </div>
          </div>
        </div>
      </div>
    </Content>
  </div>
</template>

<style lang="scss" scoped>
.group {
  width: 100%;

  .export-button {
    width: 200px;
  }

  .edit-link {
    margin-left: 12px;
    padding: 4px 8px;
    border-radius: 4px;
    color: $color-green600;

    &:hover {
      background: $color-gray200;
    }
  }

  .group-content {
    display: flex;
    flex-direction: column;

    .mark-page-content {
      display: grid;
      grid-template-columns: auto auto;
      grid-gap: 16px;
      justify-content: start;
    }

    .action-bar {
      display: flex;
      align-items: center;
      width: var(--layout-content-width, 948px);
      height: 32px;
      margin-bottom: 16px;
      justify-content: space-between;

      .left-side-container {
        display: flex;
        align-items: center;
        width: 616px;

        .c-text-regular--s {
          color: $color-gray600;
        }

        .o-memo-filter {
          margin-left: 16px;
        }

        .group-survey-button {
          margin-left: auto;
        }
      }

      .c-text-regular--s {
        color: $color-gray600;
      }
    }

    .filter-container {
      display: flex;
      gap: 16px;
      align-items: center;
      margin-bottom: 16px;
    }

    .article-area {
      width: 616px;

      .article-card {
        margin-bottom: 12px;
        cursor: pointer;
      }
    }

    .no-marks {
      display: flex;
      padding: 8px 32px;
      background-color: #fff;
      border: 1px solid $color-gray400;
      border-radius: 4px;
      justify-content: space-between;

      .content {
        display: flex;
        flex-direction: column;
        justify-content: center;
      }

      img {
        width: 100px;
        height: 100px;
      }
    }

    .description-container {
      width: 288px;

      .inner {
        background-color: #fff;
        border: 1px solid $color-gray400;
        padding: 16px;
        border-radius: 4px;
        margin-bottom: 12px;

        & > *:not(:last-child) {
          margin-bottom: 8px;
        }

        .description {
          word-wrap: break-word;
          white-space: pre-wrap;
        }

        .no-description {
          color: $color-gray600;
        }
      }
    }

    .o-header {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .member-list-link {
        color: $color-green600;
        padding: 4px 8px;

        &:hover {
          background-color: $color-gray200;
          border-radius: 4px;
          cursor: pointer;
        }
      }
    }
  }

  .side-content {
    box-sizing: border-box;
    position: sticky;
    top: calc(var(--layout-sticky-top-under-tabs, 112px) + 16px);
    width: 316px;
    max-height: 80vh;
    overflow-y: auto;
    .side-content-body {
      width: 100%;
    }
  }

  .selected-tags {
    width: var(--layout-content-width, 948px);
    margin-bottom: 16px;
  }
}
</style>
